import api from './api';

export interface SPOPRevenueData {
  spop_id: string | null;
  spop_name: string | null;
  area_code: string | null;
  paid_bills: number;
  pending_bills: number;
  overdue_bills: number;
  paid_amount: number;
  pending_amount: number;
  overdue_amount: number;
  total_amount: number;
  total_bills: number;
}

export interface SPOPRevenueSummary {
  total_bills: number;
  total_paid_bills: number;
  total_pending_bills: number;
  total_overdue_bills: number;
  total_amount: number;
  total_paid_amount: number;
  total_pending_amount: number;
  total_overdue_amount: number;
}

export interface SPOPRevenueResponse {
  success: boolean;
  data: {
    spops: SPOPRevenueData[];
    summary: SPOPRevenueSummary;
    period: {
      month: number;
      year: number;
    };
  };
}

export interface SPOPRevenueFilters {
  month?: string;
  year?: string;
  spop_id?: string;
}

class SPOPRevenueService {
  // Get SPOP revenue data
  async getSPOPRevenue(filters: SPOPRevenueFilters = {}): Promise<SPOPRevenueResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/spop-revenue?${params.toString()}`);
    return response.data;
  }

  // Export SPOP revenue to Excel
  async exportSPOPRevenue(filters: SPOPRevenueFilters): Promise<void> {
    const params = new URLSearchParams();
    
    if (filters.month) {
      params.append('month', filters.month);
    }
    if (filters.year) {
      params.append('year', filters.year);
    }
    if (filters.spop_id) {
      params.append('spop_id', filters.spop_id);
    }
    
    let url = '/spop-revenue/export';
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await api.get(url, {
      responseType: 'blob',
    });
    
    // Create download link
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    // Generate filename with timestamp
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/[:-]/g, '');
    let filename = `spop_revenue_export_${timestamp}.xlsx`;
    
    // Add filter info to filename
    if (filters.month || filters.year) {
      let filterInfo = '';
      if (filters.month) filterInfo += `_month${filters.month}`;
      if (filters.year) filterInfo += `_${filters.year}`;
      filename = `spop_revenue${filterInfo}_${timestamp}.xlsx`;
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Get SPOP revenue summary only
  async getSPOPRevenueSummary(filters: SPOPRevenueFilters = {}): Promise<any> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`/spop-revenue/summary?${params.toString()}`);
    return response.data;
  }
}

export default new SPOPRevenueService();
